import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { vitalAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiHeart, FiPlus, FiX, FiTrendingUp, FiTrendingDown, FiActivity, FiUploadCloud } from 'react-icons/fi';
import toast from 'react-hot-toast';
import CsvUploader from '../components/CsvUploader';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

const Vitals = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { canCreate, canEdit, isAdmin } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [filters, setFilters] = useState({ type: '', region: '' });
  const [form, setForm] = useState({
    type: 'birth', date: new Date().toISOString().split('T')[0],
    location: { region: 'Northeast', county: '', state: 'NY' },
    demographics: { age: '', gender: 'female', race: 'White' },
    birthData: { motherAge: '', birthWeight: '', gestationalAge: '' },
    deathData: { causeOfDeath: '', mannerOfDeath: 'natural' }
  });

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    try {
      const [recs, st] = await Promise.all([vitalAPI.getAll(filters), vitalAPI.getStats()]);
      setRecords(recs.data.records || recs.data || []);
      setStats(st.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        demographics: { ...form.demographics, age: Number(form.demographics.age) },
        ...(form.type === 'birth' ? { birthData: { motherAge: Number(form.birthData.motherAge), birthWeight: Number(form.birthData.birthWeight), gestationalAge: Number(form.birthData.gestationalAge) } } : {}),
        ...(form.type === 'death' ? { deathData: form.deathData } : {})
      };
      await vitalAPI.create(payload);
      toast.success('Vital record added');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  const { totalBirths = 0, totalDeaths = 0, avgBirthWeight = 0, avgMotherAge = 0, causeOfDeath = [], byRegion = [], byGender = [], infantMortality = 0 } = stats || {};

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Vital Statistics</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Birth/death records, maternal health, and life expectancy data</p>
        </div>
        <div className="page-header-actions" style={{ display: 'flex', gap: 10 }}>
          {canCreate && <button onClick={() => setShowCsvUpload(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#f0f9ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiUploadCloud size={18} /> CSV Upload
          </button>}
          {canCreate && <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            <FiPlus size={18} /> Add Record
          </button>}
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { icon: FiHeart, label: 'Total Births', value: totalBirths, color: '#ec4899' },
          { icon: FiTrendingDown, label: 'Total Deaths', value: totalDeaths, color: '#64748b' },
          { icon: FiActivity, label: 'Avg Birth Weight', value: `${avgBirthWeight.toFixed(0)}g`, color: '#3b82f6' },
          { icon: FiTrendingUp, label: 'Avg Mother Age', value: avgMotherAge.toFixed(1), color: '#8b5cf6' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{s.label}</p>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{s.value}</p>
              </div>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon size={22} color={s.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Leading Causes of Death</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={causeOfDeath.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="_id" tick={{ fontSize: 11 }} width={120} />
              <Tooltip />
              <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Records by Region</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byRegion}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="births" fill="#ec4899" radius={[4, 4, 0, 0]} name="Births" />
              <Bar dataKey="deaths" fill="#64748b" radius={[4, 4, 0, 0]} name="Deaths" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters & Table */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} style={selectStyle}>
          <option value="">All Types</option>
          <option value="birth">Births</option>
          <option value="death">Deaths</option>
        </select>
        <select value={filters.region} onChange={e => setFilters({ ...filters, region: e.target.value })} style={selectStyle}>
          <option value="">All Regions</option>
          {['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r}>{r}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Type', 'Date', 'Region', 'State', 'Gender', 'Race', 'Age', 'Details'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r._id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: r.type === 'birth' ? '#fce7f3' : '#f1f5f9', color: r.type === 'birth' ? '#be185d' : '#475569' }}>
                      {r.type === 'birth' ? '👶 Birth' : '📋 Death'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{new Date(r.date).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.location?.region}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.location?.state}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, textTransform: 'capitalize' }}>{r.demographics?.gender}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.demographics?.race}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.demographics?.age}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                    {r.type === 'birth' ? `Mother: ${r.birthData?.motherAge}yo, ${r.birthData?.birthWeight}g` : r.deathData?.causeOfDeath}
                  </td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Add Vital Record</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Record Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                  <option value="birth">Birth</option>
                  <option value="death">Death</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Date</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required style={inputStyle} />
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Region</label>
                  <select value={form.location.region} onChange={e => setForm({ ...form, location: { ...form.location, region: e.target.value } })} style={inputStyle}>
                    {['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <input value={form.location.state} onChange={e => setForm({ ...form, location: { ...form.location, state: e.target.value } })} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Age</label>
                  <input type="number" value={form.demographics.age} onChange={e => setForm({ ...form, demographics: { ...form.demographics, age: e.target.value } })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Gender</label>
                  <select value={form.demographics.gender} onChange={e => setForm({ ...form, demographics: { ...form.demographics, gender: e.target.value } })} style={inputStyle}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Race</label>
                  <select value={form.demographics.race} onChange={e => setForm({ ...form, demographics: { ...form.demographics, race: e.target.value } })} style={inputStyle}>
                    {['White', 'Black', 'Hispanic', 'Asian', 'Native American', 'Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {form.type === 'birth' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div><label style={labelStyle}>Mother Age</label><input type="number" value={form.birthData.motherAge} onChange={e => setForm({ ...form, birthData: { ...form.birthData, motherAge: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Birth Weight (g)</label><input type="number" value={form.birthData.birthWeight} onChange={e => setForm({ ...form, birthData: { ...form.birthData, birthWeight: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Gestational Age</label><input type="number" value={form.birthData.gestationalAge} onChange={e => setForm({ ...form, birthData: { ...form.birthData, gestationalAge: e.target.value } })} style={inputStyle} /></div>
                </div>
              )}
              {form.type === 'death' && (
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div><label style={labelStyle}>Cause of Death</label>
                    <select value={form.deathData.causeOfDeath} onChange={e => setForm({ ...form, deathData: { ...form.deathData, causeOfDeath: e.target.value } })} style={inputStyle}>
                      <option value="">Select</option>
                      {['Heart Disease', 'Cancer', 'Stroke', 'Respiratory Disease', 'Diabetes', 'Accident', 'Alzheimer\'s', 'Other'].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Manner of Death</label>
                    <select value={form.deathData.mannerOfDeath} onChange={e => setForm({ ...form, deathData: { ...form.deathData, mannerOfDeath: e.target.value } })} style={inputStyle}>
                      {['natural', 'accident', 'homicide', 'suicide', 'undetermined'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <button type="submit" style={{ width: '100%', padding: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>Add Record</button>
            </form>
          </div>
        </div>
      )}

      {showCsvUpload && (
        <CsvUploader
          title="Vital Records"
          templateColumns={['type','date','region','state','county','age','gender','race','mother_age','birth_weight','gestational_age','cause_of_death','manner_of_death']}
          templateSampleRows={[
            ['birth','2024-06-15','Northeast','NY','County 1','0','female','White','28','3200','39','',''],
            ['birth','2024-07-01','Southeast','FL','County 5','0','male','Hispanic','32','3500','40','',''],
            ['death','2024-08-10','Midwest','IL','County 8','72','male','Black','','','','Heart Disease','natural']
          ]}
          onUpload={(formData) => vitalAPI.bulkImport(formData)}
          onClose={() => { setShowCsvUpload(false); fetchData(); }}
        />
      )}
    </div>
  );
};

const selectStyle = { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 };
const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default Vitals;
